import { useState } from 'react'
import { useApp } from '@/store/useApp'
import { SCHOOL_NAMES, type ConservativeLevel } from '@/domain/types'

const LEVEL_LABELS: Record<ConservativeLevel, string> = {
  aggressive: '激进',
  normal: '中性',
  conservative: '保守'
}

const LEVEL_HINTS: Record<ConservativeLevel, string> = {
  aggressive: '偏乐观，冲线建议更多',
  normal: '默认，平衡冲稳保',
  conservative: '偏谨慎，保底优先'
}

export function HeatSlidersSection() {
  const [open, setOpen] = useState(false)
  const { heat, setHeat, resetHeat, conservativeLevel, setConservativeLevel } = useApp()

  return (
    <section className="card">
      {/* 标题行：点击展开/折叠 */}
      <button
        className="flex items-center justify-between w-full"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div>
          <h2 className="section-title mb-0 text-left">敏感性分析（高级）</h2>
          {!open && (
            <p className="text-ink-400 text-xs mt-0.5 text-left">
              模拟报名热度变化 · 调整冲稳保判定保守度
            </p>
          )}
        </div>
        <span className="text-ink-400 text-sm ml-4 shrink-0">{open ? '收起 ▴' : '展开 ▾'}</span>
      </button>

      {open && (
        <div className="mt-5">
          <p className="text-ink-500 text-sm mb-5 leading-relaxed">
            拉动滑块模拟"今年大家是否更挤某校"，实时观察概率如何变化。
            区间越窄代表该校预测越稳定。
          </p>

          <div className="space-y-4 mb-5">
            {SCHOOL_NAMES.map((s) => (
              <HeatSlider
                key={s}
                label={s}
                value={heat[s]}
                onChange={(v) => setHeat(s, v)}
              />
            ))}
          </div>

          <div className="flex justify-end mb-4">
            <button onClick={resetHeat} className="text-brand-700 text-sm active:opacity-70">
              重置所有热度
            </button>
          </div>

          <div className="pt-4 border-t border-ink-100">
            <div className="text-ink-600 text-sm font-medium mb-1">「冲 / 稳 / 保」判定风格</div>
            <div className="text-ink-400 text-xs mb-3">影响各校标签的划分阈值，不改变概率本身</div>
            <div className="flex gap-2">
              {(['aggressive', 'normal', 'conservative'] as ConservativeLevel[]).map((lv) => (
                <button
                  key={lv}
                  onClick={() => setConservativeLevel(lv)}
                  className={
                    'flex-1 py-2.5 rounded-xl text-sm flex flex-col items-center gap-0.5 transition ' +
                    (lv === conservativeLevel
                      ? 'bg-brand-600 text-white'
                      : 'bg-ink-100 text-ink-700 active:bg-ink-200')
                  }
                >
                  <span className="font-medium">{LEVEL_LABELS[lv]}</span>
                  <span className={`text-[10px] leading-tight ${lv === conservativeLevel ? 'text-brand-100' : 'text-ink-400'}`}>
                    {LEVEL_HINTS[lv]}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

function HeatSlider({
  label,
  value,
  onChange
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  const percent = ((value - 0.7) / (1.3 - 0.7)) * 100
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-ink-800">{label} 热度</span>
        <span className="text-ink-600 text-sm tabular-nums">{value.toFixed(2)}×</span>
      </div>
      <input
        type="range"
        min={0.7}
        max={1.3}
        step={0.05}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-brand-600 h-2"
        style={{
          background: `linear-gradient(to right, #c4b5fd 0%, #c4b5fd ${percent}%, #e2e8f0 ${percent}%, #e2e8f0 100%)`,
          borderRadius: 999,
          appearance: 'none'
        }}
      />
      <div className="flex justify-between text-xs text-ink-400 mt-0.5">
        <span>0.7× 冷</span>
        <span>1.0× 基准</span>
        <span>1.3× 热</span>
      </div>
    </div>
  )
}
